import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import Button from "../../components/Button-Signup";
import Input from "../../components/Input-Signup";
import Betmlogo from "../../assets/BETM_Logo.png";
// import SignupLeft from "../../assets/SignupLeft.png";
import 'font-awesome/css/font-awesome.min.css';
import Slideshow from './Slideshow';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from "@datadog/browser-rum";
import { setDatadogUser } from "../../components/DataDog/setDatadogUser";
import { useEntitlements } from "../../components/DataContext/EntitlementsContext"; 


const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;
const tokenUrl = process.env.REACT_APP_POWERBI_EMBED_API_URL;
const product = "AMP-UI";
const subscriptionKey = process.env.REACT_APP_SUBSCRIPTION_KEY_ENTITLEMENTS;


const SignUp: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isEmailSubmitted, setIsEmailSubmitted] = useState<boolean>(false);
  const [loginName, setLoginName] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { refetchEntitlements } = useEntitlements();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const [showSignupModal, setShowSignupModal] = useState(false);

  

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail");
    if (!storedEmail) {
      setEmail("");
    }
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(emailRegex.test(e.target.value) ? "" : "Please enter a valid email address.");
  };

  const handleSignUp = () => {
    if (!email || !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      datadogLogs.logger.warn("Invalid email entered during signup", { email });
      return;
    }
    
    localStorage.setItem("userEmail", email);
    setIsEmailSubmitted(true);
    
    datadogLogs.logger.info("User signed up with email", { email });
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    window.location.reload(); 
  };
  
  const handleLogin = async () => {
    const { cacheDB } = await import('../../components/CacheDB/cacheDB');
    try {
      datadogLogs.logger.info("Login attempt started");

      const loginResponse = await instance.loginPopup(loginRequest);
      if (loginResponse) {
        // Explicitly set the active account so ProtectedRoute can find it
        // synchronously via instance.getActiveAccount() on the next render.
        instance.setActiveAccount(loginResponse.account);

        const email = loginResponse.account.username;
        const name = loginResponse.account.name || "Unknown User";
        const localAccountId = loginResponse.account.localAccountId;

        const user = {
          id: localAccountId || email,
          name,
          email,
        };
        await cacheDB.responses.clear();
        setDatadogUser(user);

        localStorage.setItem("userEmail", email);
        localStorage.setItem("userObjectId", localAccountId);

        await refetchEntitlements();

        datadogLogs.logger.info("User logged in successfully", { email });

        // Redirect back to the page the user originally tried to access
        const fromState = (location.state as any)?.from?.pathname;
        const fromStorage = sessionStorage.getItem("postLoginRedirect");
        sessionStorage.removeItem("postLoginRedirect");
        const destination = fromState || fromStorage || "/landing";
        navigate(destination);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        datadogLogs.logger.error("Login failed", { error: error.message });
      } else {
        datadogLogs.logger.error("Login failed", { error: String(error) });
      }
  
      alert("Login failed, please try again.");
    }
  };
  
  
  const handleCreateAccount = async () => {
    if (!email || !emailRegex.test(email)) {
      setWarningMessage("Please enter a valid email address");
      setShowWarningDialog(true);
      return;
    }
    
    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 5);
    
    const formatDate = (date: Date): string => date.toISOString().split("T")[0]; 
    
    const effectiveDate = formatDate(today);
    const expiryDate = formatDate(nextYear);
    
    
    const postData = {
      invitedUserDisplayName: loginName,
      invitedUserEmailAddress: email,
      first_name: firstName,
      last_name: lastName,
      company_id: 0,
      enabled: "Y",
      default_access_group: "READ",
      effective_date: effectiveDate,
      expiry_date: expiryDate,
      locked: "N",
      sys_created_by: email,
      sys_modified_by: email,
    };
  
    datadogLogs.logger.info("Creating account", { email });
  
    try {
    
      const tokenResponse = await fetch(`${tokenUrl}/api/generateAzureAccessToken?code=amp-ui-access`, {
        method: "GET",
      });
    
      if (!tokenResponse.ok) {
        throw new Error("Failed to fetch access token");
      }
    
      const tokenData = await tokenResponse.json();
      const accessToken = tokenData.accessToken;
    
    
      const response = await fetch(`${apiUrl}/user/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Product": product,
          "Ocp-Apim-Subscription-Key": subscriptionKey ?? "", 
          "Authorization": `Bearer ${accessToken}`,             
        },
        body: JSON.stringify(postData),
      });
    
      if (response.ok) {
        setShowModal(true);
        datadogLogs.logger.info("Account created successfully", { email });
      } else {
        datadogLogs.logger.warn("Account creation failed", { email });
        setWarningMessage("Failed to create user");
        setShowWarningDialog(true);
      }
    } catch (error: unknown) {
      console.error("Error:", error);
    
      const errMsg = error instanceof Error ? error.message : String(error);
      datadogLogs.logger.error("Error creating account", { email, error: errMsg });
    
      setWarningMessage("Failed to create user");
      setShowWarningDialog(true);
    }
  };
  
  

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen bg-white ">
      <div className="hidden md:block relative">
      <Slideshow />
      </div>

      <div className="flex flex-col justify-center items-center p-6 md:p-8">
        <div className="w-full max-w-md p-8 bg-white rounded-xl shadow-2xl text-center">
          <div className="flex justify-center mb-6">
            <img src={Betmlogo} alt="BETM Logo" className="w-56" />
          </div>

          
          {!showSignupModal ? (
            <>
              {/* LOGIN UI */}
              <Button
                label="Log In"
                onClick={() => {
                  datadogRum.addAction("login_clicked", { element: "Log in" });
                  handleLogin();
                }}
                className="font-poppins w-11/12 py-3 bg-blue-800 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
              />

              
            </>
          ) : (
            <>
              {/* SIGN UP UI */}
              <h1 className="text-2xl font-semibold text-gray-800 mb-4 font-poppins">
                Sign Up Account
              </h1>

              <p className="text-sm text-gray-600 mb-6 font-poppins">
                Please provide the email ID to be used as your username
              </p>

              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="username@domain.com"
                className="font-poppins w-full p-3 mb-4 rounded-xl border border-gray-300 bg-gray-100 focus:ring-2 focus:ring-blue-400"
              />

              <Input
                id="loginName"
                type="text"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder="Login Name"
                className="font-poppins w-full p-3 mb-4 rounded-xl border border-gray-300 bg-gray-100 focus:ring-2 focus:ring-blue-400"
              />

              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className="font-poppins w-full p-3 mb-4 rounded-xl border border-gray-300 bg-gray-100 focus:ring-2 focus:ring-blue-400"
              />

              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className="font-poppins w-full p-3 mb-6 rounded-xl border border-gray-300 bg-gray-100 focus:ring-2 focus:ring-blue-400"
              />

              <Button
                label="Create Account"
                onClick={handleCreateAccount}
                className="font-poppins w-full py-3 bg-blue-800 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
              />

              <button
                onClick={() => setShowSignupModal(false)}
                className="mt-4 text-sm text-gray-600 hover:underline"
              >
                Cancel
              </button>
            </>
          )}

          
        </div>
      </div>
      {/* Warning Dialog */}
      {showWarningDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-xl shadow-xl w-80 text-center">
            <div className="flex items-center justify-center mb-3">
              <i className="fa fa-exclamation-triangle text-red-500 text-3xl"></i>
              <h2 className="text-xl font-semibold text-gray-800 ml-2">Warning</h2>
            </div>
            <p className="text-gray-700 mb-4">{warningMessage}</p>
            <button
              className="mt-2 w-full  bg-blue-800 text-white py-2 rounded-xl hover:bg-blue-700 py-2"
              onClick={() => setShowWarningDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-80 text-center">
            <div className="flex justify-center items-center mb-4">
              <i className="fa fa-check-circle text-green-500 text-4xl"></i>
              <h2 className="text-xl font-semibold text-gray-800 ml-3">Success!</h2>
            </div>
            <p className="text-gray-700 mb-4">
  Welcome! User <span className="font-semibold text-gray-900">{email}</span> has been successfully created. Please wait sometime to activate your account.
</p>
            <button className="mt-6 w-full bg-blue-800 text-white py-2 rounded-xl hover:bg-blue-700" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      )}



    </div>
  );
};

export default SignUp;
